<script lang="ts">
	/* https://svelte-modals.mattjennings.io/api */
	import { modals } from 'svelte-modals'
	import Button from '#lib/atoms/Button.svelte'
	import { t } from '$i18n/i18n'
	import { fly } from 'svelte/transition'
	import IconCheckmark from '$svg/icon-checkmark.svg?component'
	import IconCross from '$svg/icon-cross.svg?component'
	import InputField from '#lib/molecules/InputField.svelte'
	import type { Catalog } from './types/catalog'
	import { catalogSchema, DefaultCatalog } from './types/catalog'
	import { fetchUtils } from '$utils/fetchUtils'
	import type { Notification } from '$types/notification'
	import { DefaultNotification, NotificationType } from '$types/notification'
	import { addNotification } from '#lib/stores.js'
	import { inputFieldFormats } from '$utils/formatters'
	import type { FontProps } from '$types/fontProps'
	import { DefaultInputFieldProps } from '$types/fontProps'
	import { getFormErrors } from '$utils/formValidation'
	import { writable, type Writable } from 'svelte/store'
	import { getContext, hasContext, onMount, setContext } from 'svelte'
	import TextArea from '#lib/atoms/TextArea.svelte'
	import { checkUnedited } from '$utils/misc'
	import Notifications from '#lib/organisms/Notifications.svelte'
	import type { FormError } from '$types/formError.ts'

	const CATALOG_FORM_ERROR_PATH_PREFIX = 'catalog.'
	const fontProps: FontProps = DefaultInputFieldProps()
	const headerFontProps: FontProps = DefaultInputFieldProps('13px', 500)

	interface Props {
		// provided by <Modals/>
		isOpen: boolean
		showCloseIcon?: boolean
		// this is basically a callback which allows to trigger an update of created or changed catalog
		update?: (data: Catalog) => Catalog
		padding?: string
		maxWidth?: string
		catalog?: Catalog // to enable editing, we can give it as argument
	}

	let {
		isOpen,
		showCloseIcon = false,
		update = (data: Catalog) => {
			return data
		},
		padding = '1rem 2.5rem',
		maxWidth = '100%',
		catalog = $bindable(DefaultCatalog())
	}: Props = $props()

	setContext('useMaterialDesign', true)

	let catalogCopy: Catalog = $state(DefaultCatalog())
	const formErrors: Writable<FormError[]> = hasContext('formErrors')
		? getContext('formErrors')
		: writable([])

	let showOverlay: boolean = $state(false)
	const overlayTitle: string = $t('UI.mmpv.materialPropertyModal.unsavedChanges.title')
	const overlayMessage: string = $t('UI.mmpv.catalogModal.unsavedChanges.message')
	const overlayButtonLabel: string = $t('UI.mmpv.materialPropertyModal.unsavedChanges.button.quit')
	let overlayAction = $state(() => {})
	let clickCount = 0

	onMount(() => {
		catalogCopy = { ...catalog }
	})

	async function updateCatalog() {
		try {
			const validatedCatalog = await catalogSchema.validate(catalog, {
				stripUnknown: false,
				abortEarly: false
			})
			if (validatedCatalog.maxDepth === null) {
				validatedCatalog.maxDepth = 3
			}
			clickCount++ // we do not want this to trigger multiple times, when the validation was correct
			if (clickCount <= 1) {
				fetchUtils
					.post(`/api/catalog/${catalog.id}`, validatedCatalog)
					.then((catalogData) => {
						const notification: Notification = DefaultNotification(
							$t('UI.mmpv.createCatalog.notification.success'),
							NotificationType.SUCCESS,
							false,
							3500
						)
						addNotification(notification)
						update(catalogData) // add catalog to catalogs
						modals.close()
					})
					.catch(fetchUtils.catchErrorAndShowNotification('modal'))
					.finally(() => (clickCount = 0))
			}
		} catch (error) {
			clickCount = 0
			const err = getFormErrors(error, CATALOG_FORM_ERROR_PATH_PREFIX)
			console.warn('ValidationError: ', err)
			$formErrors = err
		}
	}

	function handleAbort() {
		let isUnedited: boolean = checkUnedited(catalog, catalogCopy)
		if (isUnedited) {
			modals.close()
		} else {
			showOverlay = true
			overlayAction = () => {
				catalog = { ...catalogCopy } // reset the catalog such that the changes are not saved with the session
				modals.close()
			}
		}
	}
</script>

{#if isOpen}
	<div
		role="dialog"
		class="modal"
		in:fly|global={{ y: -100, duration: 600 }}
		out:fly|global={{ y: -1000, duration: 600 }}
	>
		<div class="contents" style="--content-padding: {padding}; max-width: {maxWidth}">
			<Notifications target="modal" position="absolute" top="0" />
			{#if showCloseIcon}
				<Button
					variant="secondary-icon"
					class="cancel-btn"
					aria-label="Cancel"
					onclick={handleAbort}
				>
					<IconCross width={24} height={24} />
				</Button>
			{/if}
			{#if showOverlay}
				<div class="modal-overlay-content">
					<div>
						<span>{@html overlayTitle}</span>
						<p>{@html overlayMessage}</p>
					</div>
					<div>
						<Button variant="secondary" color="red" onclick={overlayAction}>
							{overlayButtonLabel}
						</Button>

						<Button
							variant="primary"
							color="red"
							onclick={() => {
								showOverlay = false
							}}
						>
							{$t('UI.mmpv.materialPropertyModal.button.continue')}
						</Button>
					</div>
				</div>
			{:else}
				<div class="actual-content">
					<h1>{@html $t('UI.mmpv.createCatalog')}</h1>
					<InputField
						bind:value={catalog.name}
						id="input-catalog-name"
						placeholder={$t('UI.mmpv.catalog.name')}
						{fontProps}
						{headerFontProps}
						format={inputFieldFormats.FULL}
						errorPath={CATALOG_FORM_ERROR_PATH_PREFIX + 'name'}
						required
					/>
					<div class="depth-and-highlighted">
						<InputField
							bind:value={catalog.maxDepth}
							id="input-catalog-depth"
							placeholder={$t('UI.mmpv.catalog.maxDepth')}
							{fontProps}
							{headerFontProps}
							errorPath={CATALOG_FORM_ERROR_PATH_PREFIX + 'maxDepth'}
							format={inputFieldFormats.FULL}
							isNumeric
							required
						/>
						<InputField
							bind:value={catalog.highlightedValue}
							id="input-catalog-indicator"
							placeholder={$t('UI.mmpv.catalog.highlighted.indicator')}
							{fontProps}
							{headerFontProps}
							errorPath={CATALOG_FORM_ERROR_PATH_PREFIX + 'maxhighlightedValue'}
							format={inputFieldFormats.FULL}
							required
						/>
					</div>

					<TextArea
						bind:text={catalog.comment}
						id="textarea-catalog-comment"
						title={$t('UI.mmpv.catalog.comment')}
						placeholder={$t('UI.mmpv.catalog.comment.ph')}
						inputFontProps={fontProps}
						{headerFontProps}
						labelGap="0.375rem"
						marginTop="0"
					/>
				</div>
				<div class="actions">
					<Button variant="secondary" color="green" onclick={handleAbort}>
						{$t('UI.button.cancel')}
					</Button>
					<Button
						id="btn-catalog-modal-add"
						variant="primary"
						color="green"
						onclick={updateCatalog}
					>
						{$t('UI.mmpv.createCatalog.modal.button')}
						<IconCheckmark width={20} height={20} />
					</Button>
				</div>
			{/if}
		</div>
	</div>
{/if}

<style>
	@import '$styles/modals.scss';

	p {
		font-family: Roboto, sans-serif;
		font-style: normal;
		line-height: 14px;
		font-weight: 400;
		font-size: 0.75rem;
		color: black;

		text-align: center;
		margin-top: 1rem;
		padding: 1rem 1rem 0;
	}

	.actual-content {
		width: 100%;
		padding: 0 1rem;

		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		gap: 12px;
	}

	.depth-and-highlighted {
		width: 100%;

		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		gap: 0.938rem;
	}

	.actions {
		display: flex;
		flex-direction: row;
		justify-content: flex-end;
		align-items: center;
		gap: 6px;

		width: 100%;
		margin-top: 1.125rem;
		padding: 0.5rem 1rem;
		border-radius: 0 0 6px 6px;
	}
</style>
